/**
 * Notifications API Service
 * Handles all notification-related API calls
 */
import api from './api';

// ============================================================================
// Types
// ============================================================================

/**
 * Notification type identifiers
 */
export type NotificationType =
  | 'transfers_detected'
  | 'sync_complete'
  | 'sync_failed'
  | 'invitation_accepted'
  | 'member_left'
  | 'balance_mismatch'
  | 'subscription_expiring'
  | 'new_transactions'
  | 'account_disconnected'
  | 'system';

/**
 * Single notification object
 */
export interface Notification {
  id: string;
  type: NotificationType | string;
  title: string;
  message?: string;
  actionUrl?: string;
  createdAt: string;
  readAt?: string;
  isRead: boolean;
}

/**
 * Response from listing notifications
 */
export interface NotificationListResponse {
  notifications: Notification[];
  unreadCount: number;
}

/**
 * Response from getting unread count
 */
export interface UnreadCountResponse {
  count: number;
}

/**
 * Response from marking all as read
 */
export interface MarkAllReadResponse {
  markedCount: number;
}

// ============================================================================
// Notification Service
// ============================================================================

export const notificationsService = {
  /**
   * Get all notifications for the current user
   */
  list: async (): Promise<NotificationListResponse> => {
    const response = await api.get<NotificationListResponse>('/notifications');

    // Transform response to ensure consistent types
    return {
      notifications: (response.notifications ?? []).map((n) => ({
        id: n.id,
        type: n.type,
        title: n.title,
        message: n.message,
        actionUrl: n.actionUrl,
        createdAt: n.createdAt,
        readAt: n.readAt,
        isRead: n.isRead ?? false,
      })),
      unreadCount: response.unreadCount ?? 0,
    };
  },

  /**
   * Get the count of unread notifications
   */
  getUnreadCount: async (): Promise<UnreadCountResponse> => {
    const response = await api.get<UnreadCountResponse>('/notifications/unread-count');
    return {
      count: response.count ?? 0,
    };
  },

  /**
   * Mark a single notification as read
   * WP-BUG-36: Uses POST per API spec (api-surface.md Section 16)
   */
  markRead: async (id: string): Promise<Notification> => {
    const response = await api.post<Notification>(`/notifications/${id}/read`);
    return {
      id: response.id,
      type: response.type,
      title: response.title,
      message: response.message,
      actionUrl: response.actionUrl,
      createdAt: response.createdAt,
      readAt: response.readAt,
      isRead: response.isRead ?? true,
    };
  },

  /**
   * Mark all notifications as read
   */
  markAllRead: async (): Promise<MarkAllReadResponse> => {
    const response = await api.post<MarkAllReadResponse>('/notifications/mark-all-read');
    return {
      markedCount: response.markedCount ?? 0,
    };
  },

  /**
   * Dismiss (hide) a notification
   */
  dismiss: async (id: string): Promise<Notification> => {
    const response = await api.delete<Notification>(`/notifications/${id}`);
    return {
      id: response.id,
      type: response.type,
      title: response.title,
      message: response.message,
      actionUrl: response.actionUrl,
      createdAt: response.createdAt,
      readAt: response.readAt,
      isRead: response.isRead ?? true,
    };
  },
};

export default notificationsService;
